import React from 'react';
import type { TextWidgetConfig } from './TextWidget';
import { FileText } from 'lucide-react';
import 'react-quill/dist/quill.snow.css';

interface TextWidgetConfigProps {
  initialConfig: Partial<TextWidgetConfig>;
  onChange: (config: TextWidgetConfig) => void;
}

export function TextWidgetConfigForm({ initialConfig = {}, onChange }: TextWidgetConfigProps) {
  const [backgroundColor, setBackgroundColor] = React.useState(initialConfig?.backgroundColor || '');
  const content = initialConfig?.content || '';
  const charCount = content.replace(/<[^>]*>/g, '').length; // Strip HTML for char count
  const showWarning = charCount > 10000;

  // Update config when background color changes
  React.useEffect(() => {
    if (onChange && backgroundColor !== initialConfig?.backgroundColor) {
      onChange({ content, backgroundColor: backgroundColor || undefined });
    }
  }, [backgroundColor]);

  return (
    <div className="space-y-4 flex-1 overflow-auto">
      {/* Widget Background - Prominent */}
      <div className="bg-gray-50 dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 rounded-lg p-3">
        <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-3">
          Widget Background
        </label>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={backgroundColor || '#ffffff'}
            onChange={(e) => setBackgroundColor(e.target.value)}
            className="w-14 h-14 rounded-lg cursor-pointer border-2 border-gray-400 dark:border-gray-500"
            title="Widget background color"
          />
          <div className="flex-1">
            <input
              type="text"
              value={backgroundColor}
              onChange={(e) => setBackgroundColor(e.target.value)}
              placeholder="#ffffff"
              className="w-full px-3 py-2 text-sm font-mono border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white mb-1"
              maxLength={7}
            />
            {backgroundColor && (
              <button
                onClick={() => setBackgroundColor('')}
                className="text-xs text-red-600 dark:text-red-400 hover:underline"
                title="Clear background"
              >
                Reset to default
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Info Panel */}
      <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <FileText size={24} className="text-purple-600 dark:text-purple-400 flex-shrink-0 mt-0.5" />
          <div className="space-y-3">
            <div>
              <h4 className="text-sm font-semibold text-purple-900 dark:text-purple-100 mb-1">
                Click to Edit
              </h4>
              <p className="text-xs text-purple-700 dark:text-purple-300">
                Click the text widget to start editing. Use the toolbar in the widget to format text.
              </p>
            </div>

            <div className="space-y-2 text-xs text-purple-700 dark:text-purple-300">
              <div className="flex items-start gap-2">
                <span className="text-purple-600 dark:text-purple-400">•</span>
                <span><strong>Font & Size:</strong> Choose from 9 fonts, sizes 10px-72px</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-purple-600 dark:text-purple-400">•</span>
                <span><strong>Title size:</strong> Use 48px, 60px, or 72px for big titles</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-purple-600 dark:text-purple-400">•</span>
                <span><strong>Double-click widget:</strong> Hide/show toolbar for more space</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-purple-600 dark:text-purple-400">•</span>
                <span><strong>Auto-save:</strong> Changes save automatically</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content Info */}
      {content && content !== '<p><br></p>' && (
        <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">Content</span>
            {showWarning && (
              <span className="text-xs text-yellow-600 dark:text-yellow-400 font-semibold">⚠️ Large</span>
            )}
          </div>
          <p className="text-xs text-gray-600 dark:text-gray-400">
            {charCount.toLocaleString()} characters
          </p>
        </div>
      )}
    </div>
  );
}
